import { useState, useCallback, useMemo } from "react";

import { ParsedTimeLock, TimeLockFilter, TimeLockStatus } from "@bridgesplit/abf-react";
import { SortDirection } from "@bridgesplit/utils";

import { useChunkedPaginationController } from "./shared";

const DEFAULT_PAGE_SIZE = 5;

export function useTimelocksPagination(vaultAddresses: string[] = []) {
    const [timelockStatus, setTimelockStatusState] = useState<TimeLockStatus>(TimeLockStatus.Pending);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Desc);
    const [includeInstant, setIncludeInstant] = useState<boolean>(false);

    const baseParams = useMemo(
        () => ({
            vaultAddresses,
            timelockStatus,
            sortDirection,
            includeInstant
        }),
        [vaultAddresses, timelockStatus, sortDirection, includeInstant]
    );

    const chunkedPagination = useChunkedPaginationController<ParsedTimeLock, TimeLockFilter>(
        baseParams,
        DEFAULT_PAGE_SIZE
    );

    const setTimelockStatus = useCallback(
        (value: TimeLockStatus | null) => {
            const newStatus = value ?? TimeLockStatus.Pending;
            setTimelockStatusState(newStatus);
            if (newStatus === TimeLockStatus.Pending) {
                setIncludeInstant(false);
            }
            chunkedPagination.resetPagination();
        },
        [chunkedPagination]
    );

    const reset = useCallback(() => {
        setTimelockStatusState(TimeLockStatus.Pending);
        chunkedPagination.resetPagination();
    }, [chunkedPagination]);

    return useMemo(
        () => ({
            ...chunkedPagination,
            params: chunkedPagination.clientPagination,
            timelockStatus,
            setTimelockStatus,
            setSortDirection,
            setIncludeInstant,
            reset
        }),
        [chunkedPagination, timelockStatus, setTimelockStatus, setSortDirection, setIncludeInstant, reset]
    );
}
